// Libs
import React from 'react';
import { withRouter, Link } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import { Form } from 'react-bootstrap';

// Services & Helpers
import GlobalStateService from 'services/GlobalStateService';
import InstructorService from 'services/InstructorService';
import LanguageService from 'services/LanguageService';
import LocationService from 'services/LocationService';
import TextHelpers from 'helpers/TextHelpers';

// Components
import Loader from 'components/common/Loader';
import PageHeader from 'components/common/PageHeader';
import InstructorList from 'components/common/InstructorList';
import SideBar from 'components/common/SideBar';
import FormChecks from 'components/common/FormChecks';

//-------------------------------------------------------------------------------------------------------------------

class InstructorListPage extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            isLoading: true,
            isLoadingFilters: true,
            filters: {
                query: '',
                geoIDs: {},
                activeStatuses: {
                    pending: true,
                    active: true,
                    inactive: true,
                    potential: true,
                    deactivated: false
                }
            },
            instructors: [],
            allInstructors: []
        };
    }
    
    componentDidMount() {
        window.scrollTo(0, 0);
        this.loadFilters();
    }

    async componentDidUpdate(oldProps) {
        if (this.props.match.params.type != oldProps.match.params.type) {
            await this.load();
        }
    }

    async loadFilters() {
        const filters = { ...this.state.filters };
        const geosReq = LocationService.listGeos();
        const languagesReq = LanguageService.list();
        const geos = await geosReq;
        const languages = await languagesReq;
        geos.forEach(g => {
            filters.geoIDs[g.id] = true;
        });
        this.setState({
            geos,
            languages,
            isLoadingFilters: false
        }, () => {
            this.load();
        });
    }

    async load() {
        const { type } = this.props.match.params;
        const { geoIDs, activeStatuses } = this.state.filters;
        GlobalStateService.setValues({ area: 'instructors-' + (type || '').toLowerCase() });
        this.setState({ isLoading: true });
        const allInstructors = await InstructorService.list({ type, geoIDs, activeStatuses });
        allInstructors.forEach(i => {
            i.searchString = TextHelpers.simplifySearchString(TextHelpers.formatInstructorName(i));
        });
        this.setState({
            allInstructors,
            instructors: this.getFiltered(allInstructors),
            isLoading: false
        });
    }

    debounceFilter() {
        clearTimeout(this.filterTimeout);
        this.filterTimeout = setTimeout(() => {
            this.setState({
                instructors: this.getFiltered(this.state.allInstructors)
            });
        }, 100);
    }

    getFiltered(allInstructors) {
        const query = TextHelpers.simplifySearchString(this.state.filters.query);
        const queryParts = query.split(' ');
        const instructors = allInstructors.filter(i => queryParts.every(qp => i.searchString.indexOf(qp) != -1));
        return instructors;
    }

    updateFilterOptions(filterName, values) {
        const filters = { ...this.state.filters };
        const filter = { ...filters[filterName] };
        for (let option in values) {
            const value = values[option];
            if (value) {
                filter[option] = value;
            } else {
                delete filter[option];
            }
        }
        filters[filterName] = filter;
        this.updateFilter(filterName, filter);
    }

    updateFilter(name, value) {
        const filters = { ...this.state.filters };
        filters[name] = value;
        this.setState({
            filters
        }, () => {
            if (name == 'query') {
                this.debounceFilter();
            } else {
                this.load();
            }
        });
    }
    
    render() {
        const { type } = this.props.match.params;
        this.loginInfo = GlobalStateService.getValue('loginInfo');

        let typeName;
        switch (type) {
            case 'Approved': typeName = 'Approved Instructors'; break;
            case 'Candidate': typeName = 'Candidates'; break;
            case 'Prospect': typeName = 'Prospects'; break;
        }

        return (
            <div className="page instructor-list-page full-width-page">

                <PageHeader hasSideBar>
                    <h1>{typeName}</h1>

                    {this.loginInfo.isAdmin &&
                        <div className="buttons">
                            <Link to={`/instructor/new?type=${type}`} className="btn btn-primary">
                                <span className="fa-solid fa-plus"></span>{' '}
                                New instructor
                            </Link>
                        </div>
                    }
                </PageHeader>

                <main>

                    <SideBar content={this.renderSideBarContent()} className="flex" />

                    <Container fluid>

                        <section>

                            {this.renderInner()}

                        </section>

                    </Container>

                </main>

            </div>
        );
    }

    renderInner() {
        const {
            isLoading,
            instructors
        } = this.state;

        if (isLoading) {
            return (<Loader fullPage />);
        }

        if (instructors.length == 0) {
            return (
                <div className="empty-text">
                    Sorry, no instructors matched your criteria
                </div>
            );
        }

        return (
            <InstructorList
                instructors={instructors}
                showSubAgency
                onClickSubAgency={subAgency => {
                    this.props.history.push(`/sub-agency/${subAgency.slug}`);
                }}
            />
        );
    }

    renderSideBarContent() {
        const {
            isLoadingFilters,
            geos,
            filters
        } = this.state;

        if (isLoadingFilters) {
            return (<Loader />);
        }

        const activeStatuses = [
            { id: 'pending', name: 'Pending' },
            { id: 'active', name: 'Active' },
            { id: 'inactive', name: 'Inactive' },
            { id: 'potential', name: 'Potential' },
            { id: 'deactivated', name: 'Deactivated' },
            { id: 'suspended', name: 'Suspended' }
        ];

        return (<>

            <Form.Group className="mb-3">
                <Form.Label>Search...</Form.Label>
                <input
                    type="search"
                    autoFocus
                    className="form-control"
                    onChange={e => this.updateFilter('query', e.target.value)}
                />
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Primary Geo</Form.Label>
                <FormChecks
                    isScrolling
                    allowSelectAllNone
                    id="geoIDs"
                    options={geos}
                    value={filters.geoIDs || {}}
                    onChange={options => {
                        this.updateFilterOptions('geoIDs', options);
                    }}
                />

            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Active Status</Form.Label>
                <FormChecks
                    isScrolling
                    allowSelectAllNone
                    id="active-status"
                    options={activeStatuses}
                    value={filters.activeStatuses}
                    onChange={(values) => this.updateFilterOptions('activeStatuses', values)}
                />
            </Form.Group>

        </>);
    }
}

export default withRouter(InstructorListPage);